import { z } from 'zod';

/**
 * Angebotstext und Spezifikation eines Artikels (ADR 0112, Schritt 3): wie die
 * Position im Angebot heißt und welche Merkmale ihre Spezifikationskarte zeigt —
 * Deutsch und Englisch. Ein Wert gilt für beide Sprachen; `valueEn` nur, wenn er
 * im Englischen anders lautet. Eine Zeile ohne Wert ist eine reine Textzeile.
 */
export const articleSpecRowSchema = z.object({
  labelDe: z.string().max(300),
  labelEn: z.string().max(300),
  value: z.string().max(500),
  valueEn: z.string().max(500),
});
export type ArticleSpecRow = z.infer<typeof articleSpecRowSchema>;

const text = z.string().trim().max(500);

export const articleOfferSpecSchema = z.object({
  offerTextDe: text,
  offerTextEn: text,
  /** Titel der Spezifikationskarte; leer = Titel der Kategorie (Mengenformel). */
  specTitleDe: text,
  specTitleEn: text,
  specs: z.array(articleSpecRowSchema).max(60),
});
export type ArticleOfferSpec = z.infer<typeof articleOfferSpecSchema>;

export const EMPTY_ARTICLE_OFFER_SPEC: ArticleOfferSpec = { offerTextDe: '', offerTextEn: '', specTitleDe: '', specTitleEn: '', specs: [] };

/** Gespeicherten Stand lesen (JSON aus der DB); Unlesbares gilt als leer. */
export function readArticleSpecs(value: unknown): ArticleSpecRow[] {
  const parsed = z.array(articleSpecRowSchema).safeParse(value);
  return parsed.success ? parsed.data : [];
}

export type ArticleSpecStatus = 'COMPLETE' | 'MISSING_EN' | 'MESSY' | 'EMPTY';

/** Zeile, die wie „Merkmal: Wert" in einer Spalte aussieht oder auf „:" endet (so kamen sie aus base44). */
export const isMessySpecRow = (row: ArticleSpecRow): boolean =>
  (!row.value && /:\s*\S/.test(row.labelDe)) || /:\s*$/.test(row.labelDe) || /:\s*$/.test(row.labelEn);

/** Stand der Pflege für die Arbeitsliste. */
export function articleSpecStatus(spec: ArticleOfferSpec): ArticleSpecStatus {
  if (!spec.specs.length) return 'EMPTY';
  if (spec.specs.some(isMessySpecRow)) return 'MESSY';
  if (!spec.offerTextEn.trim() || spec.specs.some((row) => row.labelDe.trim() && !row.labelEn.trim())) return 'MISSING_EN';
  return 'COMPLETE';
}

/** „Merkmal: Wert" in die Wert-Spalte aufteilen, End-Doppelpunkte entfernen. */
export function cleanUpSpecRows(rows: readonly ArticleSpecRow[]): ArticleSpecRow[] {
  const strip = (label: string) => label.replace(/:\s*$/, '').trim();
  return rows.map((row) => {
    const de = !row.value ? /^(.*?):\s*(.+)$/.exec(row.labelDe) : null;
    if (!de) return { ...row, labelDe: strip(row.labelDe), labelEn: strip(row.labelEn) };
    const en = /^(.*?):\s*(.+)$/.exec(row.labelEn);
    const value = de[2]!.trim();
    const valueEn = en && en[2]!.trim() !== value ? en[2]!.trim() : row.valueEn;
    return { labelDe: de[1]!.trim(), labelEn: en ? en[1]!.trim() : strip(row.labelEn), value, valueEn };
  });
}

/** Aus dem alten Format (Bezeichnung/Wert je Sprache, z. B. base44) in Zeilen mit gemeinsamem Wert. */
export function specRowsFromLocalized(specs: { de: { label: string; value: string }[]; en: { label: string; value: string }[] } | null | undefined): ArticleSpecRow[] {
  if (!specs) return [];
  const length = Math.max(specs.de.length, specs.en.length);
  return Array.from({ length }, (_, index) => {
    const de = specs.de[index] ?? { label: '', value: '' };
    const en = specs.en[index] ?? { label: '', value: '' };
    const value = de.value || en.value;
    return { labelDe: de.label, labelEn: en.label, value, valueEn: en.value && en.value !== value ? en.value : '' };
  });
}

/** Zeilen → Bezeichnung/Wert je Sprache, wie Kalkulation und Angebot sie speichern. */
export function specRowsToLocalized(rows: readonly ArticleSpecRow[]): { de: { label: string; value: string }[]; en: { label: string; value: string }[] } {
  return {
    de: rows.map((row) => ({ label: row.labelDe, value: row.value })),
    en: rows.map((row) => ({ label: row.labelEn || row.labelDe, value: row.valueEn || row.value })),
  };
}
